import { useCallback, useRef, useState } from "react";
import ApiService from "../api/apiService";

const clampLimit = (limit, fallback = 50) => {
  const numeric = Number(limit);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.min(Math.max(numeric, 1), 100);
};

const dedupeByIdPreserveOrder = (messages = []) => {
  const seen = new Set();
  const output = [];

  for (const item of messages) {
    const id = item?.id;
    if (id === undefined || id === null) {
      output.push(item);
      continue;
    }

    if (seen.has(id)) continue;
    seen.add(id);
    output.push(item);
  }

  return output;
};

const mergePrependDedupe = (incoming = [], existing = []) =>
  dedupeByIdPreserveOrder([...(incoming || []), ...(existing || [])]);

const mergeAppendDedupe = (existing = [], incoming = []) =>
  dedupeByIdPreserveOrder([...(existing || []), ...(incoming || [])]);

function useMessageJumpWindow({ scrollContainerRef } = {}) {
  const [messages, setMessages] = useState([]);
  const [anchorIndex, setAnchorIndex] = useState(null);
  const [anchorMessageId, setAnchorMessageId] = useState(null);
  const [conversationId, setConversationId] = useState(null);

  const [hasOlder, setHasOlder] = useState(false);
  const [hasNewer, setHasNewer] = useState(false);
  const [olderCursor, setOlderCursor] = useState(null);
  const [newerCursor, setNewerCursor] = useState(null);

  const [isJumpMode, setIsJumpMode] = useState(false);
  const [isJumpLoading, setIsJumpLoading] = useState(false);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [isLoadingNewer, setIsLoadingNewer] = useState(false);
  const [error, setError] = useState(null);

  const inFlightRef = useRef({
    jump: false,
    older: false,
    newer: false,
  });

  const resetJumpWindow = useCallback(() => {
    setMessages([]);
    setAnchorIndex(null);
    setAnchorMessageId(null);
    setConversationId(null);
    setHasOlder(false);
    setHasNewer(false);
    setOlderCursor(null);
    setNewerCursor(null);
    setIsJumpMode(false);
    setIsJumpLoading(false);
    setIsLoadingOlder(false);
    setIsLoadingNewer(false);
    setError(null);
  }, []);

  const jumpToMessage = useCallback(
    async (targetConversationId, targetMessageId, options = {}) => {
      if (!targetConversationId || !targetMessageId) {
        return {
          success: false,
          reason: "invalid-args",
        };
      }

      if (inFlightRef.current.jump) {
        return {
          success: false,
          reason: "busy",
        };
      }

      inFlightRef.current.jump = true;
      setIsJumpLoading(true);
      setError(null);

      try {
        const before = clampLimit(options.before ?? 40, 40);
        const after = clampLimit(options.after ?? 40, 40);

        const response = await ApiService.v3.message.getMessagesAround(
          targetConversationId,
          targetMessageId,
          before,
          after,
        );

        if (response?.httpStatus === 404) {
          const notFoundError = {
            code: "message-not-found",
            message: "Message not found in this conversation",
          };
          setError(notFoundError);
          setIsJumpMode(false);
          return {
            success: false,
            reason: "not-found",
            error: notFoundError,
          };
        }

        if (!response?.success) {
          const jumpError = {
            code: "jump-request-failed",
            message: "Unable to load target message window",
          };
          setError(jumpError);
          setIsJumpMode(false);
          return {
            success: false,
            reason: "request-failed",
            error: jumpError,
          };
        }

        const aroundMessages = dedupeByIdPreserveOrder(response.messages || []);

        const resolvedAnchorIndex =
          Number.isInteger(response.anchorIndex) && response.anchorIndex >= 0
            ? response.anchorIndex
            : aroundMessages.findIndex((msg) => String(msg?.id) === String(targetMessageId));

        const resolvedAnchorId =
          aroundMessages?.[resolvedAnchorIndex]?.id ??
          response.anchor_message_id ??
          targetMessageId;

        setConversationId(targetConversationId);
        setMessages(aroundMessages);
        setAnchorIndex(resolvedAnchorIndex >= 0 ? resolvedAnchorIndex : 0);
        setAnchorMessageId(resolvedAnchorId);
        setHasOlder(Boolean(response.hasOlder));
        setHasNewer(Boolean(response.hasNew));
        setOlderCursor(response.older_cursor || null);
        setNewerCursor(response.newer_cursor || null);
        setIsJumpMode(true);

        return {
          success: true,
          targetId: String(resolvedAnchorId),
          anchorIndex: resolvedAnchorIndex,
          response,
        };
      } catch (err) {
        const jumpError = {
          code: "jump-request-error",
          message: "Unable to load target message window",
          error: err,
        };
        setError(jumpError);
        setIsJumpMode(false);
        return {
          success: false,
          reason: "request-error",
          error: jumpError,
        };
      } finally {
        inFlightRef.current.jump = false;
        setIsJumpLoading(false);
      }
    },
    [],
  );

  const loadOlder = useCallback(
    async (limit = 50) => {
      if (!isJumpMode || !conversationId || !hasOlder || inFlightRef.current.older) {
        return { success: false, reason: "not-available" };
      }

      if (!olderCursor?.before_createdAt) {
        setHasOlder(false);
        return { success: false, reason: "missing-cursor" };
      }

      inFlightRef.current.older = true;
      setIsLoadingOlder(true);

      const container = scrollContainerRef?.current || null;
      const prevScrollHeight = container?.scrollHeight ?? 0;
      const prevScrollTop = container?.scrollTop ?? 0;

      try {
        const safeLimit = clampLimit(limit, 50);
        const response = await ApiService.v3.message.getOlderMessages(
          conversationId,
          olderCursor.before_createdAt,
          olderCursor.before_id,
          safeLimit,
        );

        if (!response?.success) {
          return { success: false, reason: "request-failed", response };
        }

        const incoming = dedupeByIdPreserveOrder(response.messages || []);

        setMessages((prev) => mergePrependDedupe(incoming, prev));
        setHasOlder(Boolean(response.hasOlder));
        setOlderCursor(response.older_cursor || null);

        if (typeof response.hasNew === "boolean") {
          setHasNewer(response.hasNew);
        }
        if (response.newer_cursor) {
          setNewerCursor(response.newer_cursor);
        }

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (!container) return;
            const nextScrollHeight = container.scrollHeight;
            container.scrollTop =
              nextScrollHeight - prevScrollHeight + prevScrollTop;
          });
        });

        return { success: true, loaded: incoming.length };
      } catch (err) {
        return { success: false, reason: "request-error", error: err };
      } finally {
        inFlightRef.current.older = false;
        setIsLoadingOlder(false);
      }
    },
    [conversationId, hasOlder, isJumpMode, olderCursor, scrollContainerRef],
  );

  const loadNewer = useCallback(
    async (limit = 50) => {
      if (!isJumpMode || !conversationId || !hasNewer || inFlightRef.current.newer) {
        return { success: false, reason: "not-available" };
      }

      if (!newerCursor?.after_createdAt) {
        setHasNewer(false);
        return { success: false, reason: "missing-cursor" };
      }

      inFlightRef.current.newer = true;
      setIsLoadingNewer(true);

      try {
        const safeLimit = clampLimit(limit, 50);
        const response = await ApiService.v3.message.getNewerMessages(
          conversationId,
          newerCursor.after_createdAt,
          newerCursor.after_id,
          safeLimit,
        );

        if (!response?.success) {
          return { success: false, reason: "request-failed", response };
        }

        const incoming = dedupeByIdPreserveOrder(response.messages || []);

        setMessages((prev) => mergeAppendDedupe(prev, incoming));
        setHasNewer(Boolean(response.hasNew));
        setNewerCursor(response.newer_cursor || null);

        if (typeof response.hasOlder === "boolean") {
          setHasOlder(response.hasOlder);
        }
        if (response.older_cursor) {
          setOlderCursor(response.older_cursor);
        }

        return { success: true, loaded: incoming.length };
      } catch (err) {
        return { success: false, reason: "request-error", error: err };
      } finally {
        inFlightRef.current.newer = false;
        setIsLoadingNewer(false);
      }
    },
    [conversationId, hasNewer, isJumpMode, newerCursor],
  );

  return {
    messages,
    anchorIndex,
    anchorMessageId,
    conversationId,
    hasOlder,
    hasNewer,
    olderCursor,
    newerCursor,
    isJumpMode,
    isJumpLoading,
    isLoadingOlder,
    isLoadingNewer,
    error,
    jumpToMessage,
    loadOlder,
    loadNewer,
    resetJumpWindow,
  };
}

export default useMessageJumpWindow;
